import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { AiDialogueSettings, District } from '@citizen-walk/shared-types';
import { useEffect, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { resolveUserAppOrigin } from '../../lib/userAppOrigin';
import { useAiDialogueSettings } from '../masters';

/**
 * AI 対話設定 panel (Sprint BG-DataSettings 15、旧 v1 `District.aiDialogue` 編集セクション踏襲)。
 *
 * 旧 v1 `old/apps/admin/src/pages/DistrictDetailPage.tsx` (line 848〜1022 の「AIとの対話」section
 * 踏襲)。旧との差分:
 *   - 旧の `hardcoded:casual / standard / walkable` (ECW 専用 cloud function 経路) は v2 で廃止、
 *     M4 `aiDialogueSettings/{id}` firestore master 一本化 (docs/04 §3-3 M4)。
 *   - 旧の `enabled` / `source` / `settingsId` 3-state を `enabled` + `settingsId` 2-state に簡素化。
 *   - 旧の `stepEnabledOverride` (Partial<AiDialogueStepEnabled>) は本 sprint では read-only 表示のみ
 *     (master 側の M4 編集 UI で step 構成を編集する設計、v2 では地区別 override は M5 経路で吸収)。
 *
 * 踏襲した点:
 *   - prompt source = aiDialogueSettings master id select (pull-down)
 *   - select 下に「現在のソース」visual 強調表示 (旧の amber bg / brand-700 text 踏襲)
 *   - 選択した preset の steps[] を read-only 一覧で表示 (旧 step 構成 UI の確認用 view 踏襲)
 *
 * v2 District 型は `aiDialogue` field をまだ持たないため、本 panel は Firestore 直書きで
 * `{ enabled, settingsId }` を merge する (district doc に sub-object として保存)。
 * 後続 sprint で shared-types に正式追加するまでの forward-compatible 設計。
 */

interface DistrictAiDialogueLocal {
  enabled: boolean;
  settingsId: string | null;
}

/** pure: District doc → 現在の aiDialogue local 値 (unknown 安全変換)。 */
export function readAiDialogueFromDistrict(d: District): DistrictAiDialogueLocal {
  const raw = (d as unknown as { aiDialogue?: unknown }).aiDialogue;
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const enabled = o.enabled === true;
    const settingsId =
      typeof o.settingsId === 'string' && o.settingsId.length > 0 ? o.settingsId : null;
    return { enabled, settingsId };
  }
  return { enabled: false, settingsId: null };
}

/** pure: 「未設定 (無効化)」を select で選んだときの payload。 */
export function buildAiDialoguePayload(enabled: boolean, settingsId: string | null) {
  if (!enabled || !settingsId) {
    return { enabled: false, settingsId: null };
  }
  return { enabled: true, settingsId };
}

/**
 * pure: 管理者用「分析ツールを開く」リンク URL を組み立てる。
 *
 * - 参加者 URL 規約 (`{userOrigin}/m/{urlToken}`、DistrictUrlQrPanel と同形) に `/analysis` を付与。
 * - `nickname` が非空なら `?adminNickname=` を付ける。user 側 AnalysisPage が読み取り、
 *   気づき・処方箋カードの表示名をこの値で上書きする (membership nickname より優先、管理者プレビュー)。
 * - `analysisEnabled === false` でも `/m/:urlToken/analysis` route 自体は到達可能なため、
 *   ユーザー非公開でも管理者はこのリンクから分析ツールを開ける。
 */
export function buildAnalysisToolUrl(origin: string, urlToken: string, nickname: string): string {
  const safeOrigin = (origin || '').replace(/\/+$/, '');
  const base = `${safeOrigin}/m/${encodeURIComponent(urlToken)}/analysis`;
  const nn = nickname.trim();
  return nn ? `${base}?adminNickname=${encodeURIComponent(nn)}` : base;
}

/** localStorage キー (地区別)。管理者プレビュー用ニックネームは端末ローカルに保持 (Firestore 非汚染)。 */
function adminNicknameStorageKey(districtId: string): string {
  return `cw.admin.analysisNickname.v1.${districtId}`;
}

function loadAdminNickname(districtId: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(adminNicknameStorageKey(districtId)) ?? '';
  } catch {
    return '';
  }
}

function saveAdminNickname(districtId: string, nickname: string): void {
  if (typeof window === 'undefined') return;
  try {
    const key = adminNicknameStorageKey(districtId);
    const v = nickname.trim();
    if (v) window.localStorage.setItem(key, v);
    else window.localStorage.removeItem(key);
  } catch {
    /* localStorage 不可 (private mode 等) は無視 */
  }
}

export interface DistrictAiDialoguePanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictAiDialoguePanel({ district, uid, canEdit }: DistrictAiDialoguePanelProps) {
  const { toast } = useToast();
  const init = readAiDialogueFromDistrict(district);
  const [settingsId, setSettingsId] = useState<string | null>(init.settingsId);
  // 分析ツールをユーザーアプリに表示するか (District.analysisEnabled、未指定は true 扱い)。
  const [analysisEnabled, setAnalysisEnabled] = useState<boolean>(district.analysisEnabled ?? true);
  const [saving, setSaving] = useState(false);
  const mastersState = useAiDialogueSettings();
  const presets: (AiDialogueSettings & { id: string })[] =
    mastersState.status === 'ready' ? mastersState.items : [];
  const selectedPreset = presets.find((p) => p.id === settingsId) ?? null;

  // 管理者プレビュー用ニックネーム (端末 localStorage)。master / 地区管理者ともに編集可。
  const [adminNickname, setAdminNickname] = useState<string>('');
  useEffect(() => {
    setAdminNickname(loadAdminNickname(district.id));
  }, [district.id]);

  const analysisToolUrl = buildAnalysisToolUrl(
    resolveUserAppOrigin(),
    district.urlToken,
    adminNickname,
  );

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = buildAiDialoguePayload(settingsId !== null, settingsId);
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          aiDialogue: payload,
          // Task 1: 分析ツール (AI 対話を含む) のユーザーアプリ表示可否。
          analysisEnabled,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'AI 対話設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function handleOpenAnalysisTool() {
    saveAdminNickname(district.id, adminNickname);
    if (typeof window !== 'undefined') {
      window.open(analysisToolUrl, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-ai-dialogue-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">💬 AI 対話設定</h3>
      <p className="text-xs text-slate-500 mb-3">
        カード生成時のAIの問いかけ文を1件選びます。未指定だとAI対話は無効になります。
      </p>

      {/* Task 1: 分析ツール(AI対話を含む)のユーザーアプリ表示可否 */}
      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={analysisEnabled}
            onChange={(e) => setAnalysisEnabled(e.target.checked)}
            disabled={!canEdit || saving}
            className="mt-0.5 h-4 w-4"
            data-testid="analysis-enabled-checkbox"
          />
          <span className="text-sm text-slate-800">
            ユーザーアプリに分析ツール（AI対話を含む）を表示する
            <span className="block text-[11px] text-slate-500 mt-0.5">
              オフにすると参加者のメニューから分析ツールが消えます。オフでも管理者は下の「分析ツールを開く」から利用できます。
            </span>
          </span>
        </label>
      </div>

      <label className="grid grid-cols-1 gap-1 mb-3">
        <span className="text-xs text-slate-500">問いかけのテンプレート</span>
        <select
          value={settingsId ?? ''}
          onChange={(e) => setSettingsId(e.target.value || null)}
          disabled={!canEdit || saving}
          className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          data-testid="ai-dialogue-settings-select"
        >
          <option value="">未設定 (AI 対話 無効)</option>
          {mastersState.status === 'loading' && (
            <option value="" disabled>
              読込中…
            </option>
          )}
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName || p.id} [{p.mode}]
            </option>
          ))}
        </select>
      </label>

      <div
        className="mt-2 mb-3 p-2 bg-warning-50 border border-warning-200 rounded text-xs"
        data-testid="ai-dialogue-current-summary"
      >
        <span className="font-semibold text-warning-700">現在のソース: </span>
        {selectedPreset ? (
          <span className="text-primary-700">
            📦 {selectedPreset.displayName} (mode: {selectedPreset.mode}, v{selectedPreset.version})
          </span>
        ) : (
          <span className="text-danger-700">未設定 — ECW モードでは card 生成時にエラー</span>
        )}
      </div>

      {/* 選択 preset の steps[] read-only 一覧 (旧の step 構成確認 UI 踏襲) */}
      {selectedPreset && selectedPreset.steps && selectedPreset.steps.length > 0 && (
        <section className="mb-3">
          <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">
            preset の step 構成 (read-only)
          </h4>
          <ul
            className="text-xs text-slate-700 space-y-1 list-disc pl-5"
            data-testid="ai-dialogue-steps-list"
          >
            {selectedPreset.steps.map((s, i) => (
              <li key={s.stepId || i}>
                <code>{s.stepId}</code>{' '}
                {s.enabled ? (
                  <span className="text-success-600">[ON]</span>
                ) : (
                  <span className="text-slate-400">[OFF]</span>
                )}
                <span className="text-slate-500"> — {s.prompt.slice(0, 60)}…</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400 mt-2">
            step 構成自体の編集は M4 「AIとの対話設定」master 画面 (master role) から。
          </p>
        </section>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="ai-dialogue-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>

      {/* Task 2: 管理者用 分析ツールリンク + 投稿ニックネーム (master / 地区管理者ともに利用可) */}
      <section className="mt-5 pt-4 border-t border-slate-200" data-testid="ai-dialogue-admin-link">
        <h4 className="text-sm font-bold text-slate-800 mb-1">🔗 分析ツールを開く（管理者用）</h4>
        <p className="text-[11px] text-slate-500 mb-3">
          ユーザーアプリに分析ツールを表示しない設定でも、管理者はここから分析ツール（AI対話）を開けます。
          下のニックネームが、管理者がここで投稿する気づき・処方箋カードの表示名になります。
        </p>

        <label className="grid grid-cols-1 gap-1 mb-3 max-w-xs">
          <span className="text-xs text-slate-500">管理者の投稿ニックネーム</span>
          <input
            type="text"
            value={adminNickname}
            onChange={(e) => setAdminNickname(e.target.value)}
            placeholder="例: 事務局"
            maxLength={40}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            data-testid="admin-nickname-input"
          />
        </label>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="secondary"
            onClick={handleOpenAnalysisTool}
            data-testid="admin-open-analysis"
          >
            分析ツールを開く ↗
          </Button>
          <a
            href={analysisToolUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-slate-400 break-all hover:text-primary-600 hover:underline"
            data-testid="admin-analysis-url"
          >
            {analysisToolUrl}
          </a>
        </div>
      </section>
    </Card>
  );
}
